"use client"

import LetakyFormFields from "@/(admin)/modules/letaky/components/LetakyFormFields";
import LetakyPublishPanel, { LetakyVisibility } from "@/(admin)/modules/letaky/components/LetakyPublishPanel";
import { useLetakyManager } from "@/(admin)/modules/letaky/hooks/useLetakyManager";
import AccessDenied from "@/components/custom-access-denied"; // Import
import { useCheckPermission } from "@/hook/use-check-permission"; // Import
import { useGuardedForm } from "@/hook/useGuardedForm";
import { useLetaky } from "@/hook/useLetaky";
import { t } from "@/languages/i18n";
import { MediaData } from "@/lib/config/types";
import { Letak, LetakRequest } from "@/types/codegen/data-contracts";
import { createPath } from "@/utils/create-path";
import { normalizeFormData } from "@/utils/normalize-form-data";
import { FormSection, FormShell, FormSkeleton, StickyActionBar } from "@/(admin)/components/form";
import { LetakyVisibilityPayload, updateLetakyVisibility } from "@/services/letaky";
import { generateLeafletSlug } from "@/components/leaflet/utils";
import { useDomainStore } from "@/stores/use-domain-store";
import { getDraftPreviewUrl } from "@/utils/admin-preview-url";
import { EyeOutlined, SaveOutlined, SendOutlined } from "@ant-design/icons";
import { Button, Form } from "antd";
import dayjs from "dayjs";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LETAKY } from "../../../../constants/admin-paths";

type SingleImage = NonNullable<NonNullable<Letak["catalogImages"]>["data"]>[number]

interface FormUpsertLetakyProps {
  onLetakyAdded?: () => void
}

const FormUpsertLetaky: React.FC<FormUpsertLetakyProps> = ({ onLetakyAdded }) => {
  const searchParams = useSearchParams()
  const id = searchParams.get("id")
  // Các mốc xuất bản đã lưu. Panel Xuất bản ghi thẳng lên server (như Post),
  // nên không nằm trong snapshot "chưa lưu" của form.
  const [visibility, setVisibility] = useState<LetakyVisibility>({
    publishedAt: null,
    scheduledAt: null,
    unpublishAt: null,
  })
  const [visibilityLoading, setVisibilityLoading] = useState(false)
  // "Tạo & Xuất bản": onFinish đọc cờ này để set publishedAt khi tạo.
  const publishOnCreateRef = useRef(false)
  // "Xem trước": lưu trước rồi mở trang public của letak (giống Post).
  const previewAfterSaveRef = useRef(false)
  const selectedDomain = useDomainStore(state => state.selectedDomain)
  const [language, setLanguages] = useState<string>("vi")
  const router = useRouter()
  const isEditMode = Boolean(id)
  const [form] = Form.useForm()
  const [isFormReady, setIsFormReady] = useState(false);
  const { hasPermission: checkPermission } = useCheckPermission()
  const { loading, fetchLetakyById, letakyDetail, updateLetaky, createLetaky } = useLetaky()

  const requiredPermission = isEditMode ? "update" : "create"
  if (!checkPermission("letak", requiredPermission)) {
    return <AccessDenied />
  }

  const {
    featuredImageValue,
    setFeaturedImageValue,
    catalogImagesValue,
    setCatalogImagesValue,
    pdfFileValue,
    setPdfFileValue,
  } = useLetakyManager()

  const catalogImages = Form.useWatch("catalogImages", form)
  const catalogCount = Array.isArray(catalogImages) ? catalogImages.length : 0
  const dateStart = Form.useWatch("dateStart", form)
  const dateEnd = Form.useWatch("dateEnd", form)

  const getNormalizedSnapshot = useCallback(() => {
    return {
      ...normalizeFormData(form.getFieldsValue(true)),
      language,
    }
  }, [form, language])


  const { isChange, checkChange, modal, message, commitSnapshot } = useGuardedForm({
    form,
    getSnapshot: getNormalizedSnapshot
  });


  useEffect(() => {
    checkChange();
  }, [language, featuredImageValue, catalogImagesValue, pdfFileValue, checkChange]);


  useEffect(() => {
    if (!isEditMode) {
      setIsFormReady(true);
    }
  }, [isEditMode]);

  useEffect(() => {
    if (isEditMode && letakyDetail) {
      form.setFieldsValue({
        title: letakyDetail.attributes?.title,
        description: letakyDetail.attributes?.description,
        featuredImage: letakyDetail.attributes?.featuredImage?.data?.id,
        catalogImages: letakyDetail.attributes?.catalogImages?.data?.map((item: SingleImage) => item.id),
        pdfFile: letakyDetail.attributes?.pdfFile?.data?.id,
        dateStart: letakyDetail.attributes?.dateStart ? dayjs(letakyDetail.attributes.dateStart) : null,
        dateEnd: letakyDetail.attributes?.dateEnd ? dayjs(letakyDetail.attributes.dateEnd) : null,
      })
      const { publishedAt, scheduledAt, unpublishAt } = letakyDetail.attributes ?? {}
      setVisibility({
        publishedAt: publishedAt ?? null,
        scheduledAt: scheduledAt ?? null,
        unpublishAt: unpublishAt ?? null,
      })

      if (letakyDetail.attributes?.locale) {
        setLanguages(letakyDetail.attributes?.locale)
      }
      if (letakyDetail.attributes?.featuredImage?.data) {
        setFeaturedImageValue([letakyDetail.attributes?.featuredImage.data as MediaData])
      }
      if (letakyDetail.attributes?.pdfFile?.data) {
        setPdfFileValue([letakyDetail.attributes?.pdfFile.data as MediaData])
      }
      if (letakyDetail.attributes?.catalogImages?.data) {
        setCatalogImagesValue(letakyDetail.attributes?.catalogImages.data as MediaData[])
      }
      setIsFormReady(true);
    }
  }, [letakyDetail])

  useEffect(() => {
    if (isFormReady) {
      const animationId = requestAnimationFrame(() => {
        commitSnapshot();
      });
      return () => cancelAnimationFrame(animationId);
    }
  }, [isFormReady, commitSnapshot]);

  const handleSubmit = async () => {
    const publishOnCreate = publishOnCreateRef.current
    publishOnCreateRef.current = false
    // Mở tab ngay trong lượt submit để không bị chặn popup; điều hướng sau khi lưu xong.
    const previewWindow = isEditMode && previewAfterSaveRef.current ? window.open("", "_blank") : null
    previewAfterSaveRef.current = false
    try {
      const values = form.getFieldsValue()
      const requestData: LetakRequest = {
        data: {
          title: values.title,
          description: values.description,
          featuredImage: values?.featuredImage,
          catalogImages: values?.catalogImages,
          pdfFile: values?.pdfFile,
          ...(values.dateStart && { dateStart: dayjs(values.dateStart).toISOString() }),
          ...(values.dateEnd && { dateEnd: dayjs(values.dateEnd).toISOString() }),
          locale: language,
          // Cập nhật không đụng tới các mốc xuất bản (panel Xuất bản lo phần đó).
          ...(!isEditMode && { publishedAt: publishOnCreate ? new Date().toISOString() : null }),
        },
      }
      if (isEditMode) {
        const saved = await updateLetaky(Number(id), requestData)
        if (saved) commitSnapshot();

        if (previewWindow) {
          if (saved) {
            // Giống Post: mint token preview ngắn hạn → /api/draft/enable bật Draft Mode
            // rồi redirect về trang letak, nhờ đó xem được cả bản nháp.
            const previewPath = `/${language}/letak/${generateLeafletSlug(values.title || "letak", String(id))}`
            try {
              previewWindow.location.href = await getDraftPreviewUrl(selectedDomain?.attributes?.domain, previewPath)
            } catch {
              previewWindow.close()
              message.error(t("Không thể mở bản xem trước"))
            }
          } else {
            previewWindow.close()
          }
        }
      } else {
        const success = await createLetaky(requestData)

        if (success) {
          commitSnapshot();
          message.success(publishOnCreate ? t("Tạo và xuất bản letak thành công!") : t("Letaky được thêm thành công!"))
          onLetakyAdded?.()
          router.push(createPath(LETAKY.LIST.PATH, searchParams))
        }
      }
    } catch (error) {
      console.error("Submit error:", error)
    }
  }

  useEffect(() => {
    if (id && isEditMode) {
      fetchLetakyById(Number(id))
    }
  }, [id])

  const canSubmit = isEditMode ? checkPermission("letak", "update") : checkPermission("letak", "create")
  const canPublishOnCreate = checkPermission("letak", "create") && checkPermission("letak", "update")

  const saveVisibility = async (payload: LetakyVisibilityPayload, successMessage: string) => {
    if (!id) return
    try {
      setVisibilityLoading(true)
      const response = await updateLetakyVisibility(Number(id), payload)
      const attrs = response?.data?.attributes ?? {}
      setVisibility(prev => ({
        publishedAt: "publishedAt" in payload ? attrs.publishedAt ?? payload.publishedAt ?? null : prev.publishedAt,
        scheduledAt: "scheduledAt" in payload ? payload.scheduledAt ?? null : prev.scheduledAt,
        unpublishAt: "unpublishAt" in payload ? payload.unpublishAt ?? null : prev.unpublishAt,
      }))
      message.success(successMessage)
    } catch {
      message.error(t("Lỗi khi cập nhật trạng thái xuất bản letak"))
    } finally {
      setVisibilityLoading(false)
    }
  }

  const handlePublishNow = (unpublishAt: string | null) =>
    saveVisibility(
      { publishedAt: new Date().toISOString(), scheduledAt: null, unpublishAt },
      t("Xuất bản letak thành công!"),
    )
  const handleSchedule = (scheduledAt: string, unpublishAt: string | null) =>
    saveVisibility({ publishedAt: null, scheduledAt, unpublishAt }, t("Lên lịch letak thành công!"))
  const handleSaveUnpublishAt = (unpublishAt: string | null) =>
    saveVisibility({ unpublishAt }, unpublishAt ? t("Đã lưu lịch gỡ bài!") : t("Đã bỏ lịch gỡ bài"))
  const handleUnpublish = () =>
    saveVisibility(
      { publishedAt: null, scheduledAt: null, unpublishAt: null },
      visibility.scheduledAt && !visibility.publishedAt
        ? t("Đã bỏ lịch đăng letak")
        : t("Hủy xuất bản letak thành công!"),
    )

  const localizations = useMemo(() => {
    type LocalizedAttrs = { localizations?: { data?: { id?: number; attributes?: { locale?: string } }[] } }
    const data = (letakyDetail?.attributes as LocalizedAttrs | undefined)?.localizations?.data
    if (!Array.isArray(data)) return []
    return data
      .filter(loc => loc?.id && loc?.attributes?.locale)
      .map(loc => ({ id: loc.id as number, locale: loc.attributes?.locale as string }))
  }, [letakyDetail])

  // Rời trang khi còn thay đổi chưa lưu → hỏi lại (giống Post).
  const guardedNavigate = (href: string) => {
    if (!isChange) {
      router.push(href)
      return
    }
    modal.confirm({
      title: t("Cảnh báo: Dữ liệu chưa được lưu!"),
      content: t("Bạn có những thay đổi chưa lưu. Nếu thoát bây giờ, dữ liệu của bạn sẽ bị mất."),
      okText: t("Thoát và Bỏ qua"),
      cancelText: t("Ở lại trang"),
      okButtonProps: { danger: true },
      onOk: () => router.push(href),
    })
  }

  if (isEditMode && !isFormReady) {
    return <FormSkeleton mainSections={3} sidebarCards={1} />
  }

  const crumbs = [
    { title: "Letáky", href: createPath(LETAKY.LIST.PATH, searchParams) },
    { title: isEditMode ? t("Chỉnh sửa") : t("Tạo mới") },
  ]

  const main = (
    <FormSection title={t("Thông tin")} collapsible={false}>
      <LetakyFormFields
        form={form}
        catalogCount={catalogCount}
        featuredImageValue={featuredImageValue}
        setFeaturedImageValue={setFeaturedImageValue}
        catalogImagesValue={catalogImagesValue}
        setCatalogImagesValue={setCatalogImagesValue}
        pdfFileValue={pdfFileValue}
        setPdfFileValue={setPdfFileValue}
      />
    </FormSection>
  )

  const sidebar = (
    <FormSection title={t("Xuất bản")} collapsible={false} compact>
      <LetakyPublishPanel
        isEditMode={isEditMode}
        visibility={visibility}
        loading={visibilityLoading}
        onPublishNow={handlePublishNow}
        onSchedule={handleSchedule}
        onSaveUnpublishAt={handleSaveUnpublishAt}
        onUnpublish={handleUnpublish}
        dateStart={dateStart ? dayjs(dateStart).toISOString() : null}
        dateEnd={dateEnd ? dayjs(dateEnd).toISOString() : null}
        language={language}
        setLanguages={setLanguages}
        localizations={localizations}
        onNavigateToLocale={locId => guardedNavigate(`/admin${LETAKY.EDIT.PATH}?id=${locId}`)}
        onCreateLocale={targetLocale => guardedNavigate(`/admin/letaky/translate?id=${id}&locale=${targetLocale}`)}
      />
    </FormSection>
  )

  const actionBar = (
    <StickyActionBar
      right={
        isEditMode ? (
          <>
            <Button
              icon={<EyeOutlined />}
              onClick={() => {
                previewAfterSaveRef.current = true
                form.submit()
              }}
              disabled={loading || !canSubmit}
            >
              {t("Xem trước")}
            </Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={() => {
                previewAfterSaveRef.current = false
                form.submit()
              }}
              loading={loading}
              disabled={!canSubmit}
            >
              {t("Cập nhật")}
            </Button>
          </>
        ) : (
          <>
            <Button
              type={canPublishOnCreate ? "default" : "primary"}
              icon={<SaveOutlined />}
              onClick={() => {
                publishOnCreateRef.current = false
                form.submit()
              }}
              loading={loading}
              disabled={!canSubmit}
            >
              {t("Tạo bản nháp")}
            </Button>
            {canPublishOnCreate && (
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={() => {
                  publishOnCreateRef.current = true
                  form.submit()
                }}
                loading={loading}
              >
                {t("Tạo & Xuất bản")}
              </Button>
            )}
          </>
        )
      }
    />
  )

  return (
    <Form labelAlign="left" layout="vertical" form={form} onFinish={handleSubmit} onValuesChange={checkChange}>
      <FormShell
        title={isEditMode ? t("Update Letáky") : t("Add New Letáky")}
        subtitle={isEditMode && letakyDetail?.attributes?.title ? letakyDetail.attributes.title : undefined}
        crumbs={crumbs}
        main={main}
        sidebar={sidebar}
        actionBar={actionBar}
      />
    </Form>
  )
}

export default FormUpsertLetaky
